import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import useScrollAnimation from '../../hooks/useScrollAnimation';
import {
  FiMonitor, FiTruck, FiHeart, FiX, FiChevronLeft, FiChevronRight, FiCamera,
  FiWifi, FiMusic, FiShield, FiGlobe, FiBook, FiHome, FiSun, FiGrid,
} from 'react-icons/fi';
import { BiAtom, BiDumbbell, BiFoodMenu, BiLibrary } from 'react-icons/bi';
import { settingsAPI } from '../../services/api';

// Must mirror the icon names offered in the admin panel's Facilities editor.
const ICON_MAP = {
  FiMonitor, BiAtom, BiLibrary, BiDumbbell, FiTruck, BiFoodMenu, FiHeart,
  FiCamera, FiWifi, FiMusic, FiShield, FiGlobe, FiBook, FiHome, FiSun, FiGrid,
};

// Full literal Tailwind class strings — must mirror the admin panel's color map.
const COLOR_MAP = {
  primary:   'bg-primary-50 text-primary-600 border-primary-100',
  secondary: 'bg-secondary-50 text-secondary-600 border-secondary-100',
  blue:      'bg-blue-50 text-blue-600 border-blue-100',
  indigo:    'bg-indigo-50 text-indigo-600 border-indigo-100',
  orange:    'bg-orange-50 text-orange-600 border-orange-100',
  pink:      'bg-pink-50 text-pink-600 border-pink-100',
  teal:      'bg-teal-50 text-teal-600 border-teal-100',
  purple:    'bg-purple-50 text-purple-600 border-purple-100',
};

const fallbackFacilities = [
  { title: 'Smart Classrooms', description: 'Interactive whiteboards and digital learning tools in every classroom.', icon: 'FiMonitor', color: 'primary' },
  { title: 'Science Labs', description: 'Well-equipped Physics, Chemistry, and Biology laboratories for hands-on learning.', icon: 'BiAtom', color: 'secondary' },
  { title: 'Computer Lab', description: 'Modern computer lab with high-speed internet for digital literacy.', icon: 'FiMonitor', color: 'blue' },
  { title: 'Library', description: 'Extensive collection of books, journals, and digital resources.', icon: 'BiLibrary', color: 'primary' },
  { title: 'Sports Complex', description: 'Multi-sport facilities including basketball, volleyball, and athletics track.', icon: 'BiDumbbell', color: 'secondary' },
  { title: 'Transportation', description: 'Safe and reliable school bus services covering all major routes.', icon: 'FiTruck', color: 'indigo' },
  { title: 'Cafeteria', description: 'Hygienic cafeteria serving nutritious meals and healthy snacks.', icon: 'BiFoodMenu', color: 'orange' },
  { title: 'Medical Room', description: 'On-campus medical facility with trained nurse and first aid support.', icon: 'FiHeart', color: 'pink' },
];

export default function FacilitiesSection() {
  const { ref, isVisible } = useScrollAnimation();
  const [facilities, setFacilities] = useState(fallbackFacilities);
  const [lightbox, setLightbox] = useState(null); // { title, photos, index }

  useEffect(() => {
    settingsAPI.get().then((res) => {
      const data = res.data?.data?.settings?.facilities || [];
      if (data.length > 0) setFacilities(data);
    }).catch(() => {});
  }, []);

  const openGallery = (facility) => {
    const photos = facility.photos || [];
    if (photos.length === 0) return;
    setLightbox({ title: facility.title, photos, index: 0 });
  };
  const closeLightbox = () => setLightbox(null);
  const prevImage = () => setLightbox((l) => l && { ...l, index: (l.index - 1 + l.photos.length) % l.photos.length });
  const nextImage = () => setLightbox((l) => l && { ...l, index: (l.index + 1) % l.photos.length });

  useEffect(() => {
    const handleKey = (e) => {
      if (!lightbox) return;
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightbox]);

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 sm:mb-14"
        >
          <p className="section-subtitle mb-2">World-Class Facilities</p>
          <h2 className="section-title mb-4">State-of-the-Art Infrastructure</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-sm sm:text-base">
            We provide excellent facilities to create an optimal learning environment
            that supports academic and personal growth.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-5">
          {facilities.map((facility, idx) => {
            const Icon = ICON_MAP[facility.icon] || FiGrid;
            const colorCls = COLOR_MAP[facility.color] || COLOR_MAP.primary;
            const photos = facility.photos || [];
            const hasPhotos = photos.length > 0;
            return (
              <motion.div
                key={facility._id || idx}
                initial={{ opacity: 0, scale: 0.92 }}
                animate={isVisible ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.45, delay: idx * 0.07 }}
                onClick={() => openGallery(facility)}
                className={`relative overflow-hidden bg-white border border-gray-100 rounded-2xl p-4 sm:p-5 group card-hover shadow-sm ${hasPhotos ? 'cursor-pointer' : 'cursor-default'}`}
              >
                <div className={`transition-opacity duration-300 ${hasPhotos ? 'group-hover:opacity-0' : ''}`}>
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center mb-3 sm:mb-4 border group-hover:scale-110 transition-transform ${colorCls}`}>
                    <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <h3 className="font-bold text-gray-800 text-xs sm:text-sm mb-1.5 sm:mb-2">{facility.title}</h3>
                  <p className="text-gray-400 text-xs leading-relaxed hidden sm:block">{facility.description}</p>
                </div>

                {hasPhotos && (
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <img src={photos[0].url} alt={facility.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-2.5 sm:p-3 flex items-center justify-between gap-2">
                      <p className="text-white text-xs font-semibold truncate">{facility.title}</p>
                      <span className="flex items-center gap-1 text-white/90 text-[10px] bg-black/40 px-1.5 py-0.5 rounded-full flex-shrink-0">
                        <FiCamera className="w-2.5 h-2.5" /> {photos.length}
                      </span>
                    </div>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            onClick={closeLightbox}
          >
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 text-white/70 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors z-10"
            >
              <FiX className="w-6 h-6" />
            </button>
            {lightbox.photos.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); prevImage(); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-3 rounded-full hover:bg-white/10 transition-colors z-10"
                >
                  <FiChevronLeft className="w-7 h-7" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); nextImage(); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-3 rounded-full hover:bg-white/10 transition-colors z-10"
                >
                  <FiChevronRight className="w-7 h-7" />
                </button>
              </>
            )}

            <motion.div
              key={lightbox.index}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-4xl w-full"
            >
              <img
                src={lightbox.photos[lightbox.index].url}
                alt={lightbox.title}
                className="w-full max-h-[75vh] object-contain rounded-2xl"
              />
              <div className="text-center mt-4">
                <h3 className="text-white font-semibold">{lightbox.title}</h3>
              </div>
              {lightbox.photos.length > 1 && (
                <div className="text-center mt-3 text-white/40 text-sm">
                  {lightbox.index + 1} / {lightbox.photos.length}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
